// cSpell:ignore Aanvulling afgerekend huurcontract maand Naam précédent rekende Trimestre Volgende volledig Vorige
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Button,
  SegmentedControl,
  TextField,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import styled from "styled-components";
import { IsoDateField } from "../../../../components/IsoDateField";
import {
  IPeriodPreset,
  periodPresets,
  TPeriodPresetId,
} from "../../../../costPeriod.utils";
import {
  endsBeforeFirstCost,
  ILeaseSettledThrough,
  settledUntilCappedStart,
  startsBeforeFirstCost,
} from "../../../../departureStart.utils";
import { todayIsoDay } from "../../../../localDay.utils";
import {
  departureEndsTooFarAhead,
  getAutoSelectedInvoiceIds,
  getDraftSettlementName,
  getPeriodStepErrors,
  getSuggestedSettlementName,
  overlappingSettlementNames,
  periodRunsPastToday,
  SETTLEMENT_NAME_MAX_LENGTH,
} from "../settlementWizard.utils";
import { fmtDate, Hint, StepHeader, Warn } from "./shared";
import { StepContext } from "./types";

const localizeError = (key?: string) =>
  key ? getLocalizedText(key) : undefined;

// Compact override on top of the "small" SegmentedControl variant, so the
// presets row does not dominate the step: it spans the width it is given in
// equal segments, its labels in sentence case without the buttons' letter
// spacing, so the cost form's five fit its popup side by side (Lewis,
// 2026-10-19). The settlement's period step looks the same. A label longer
// than its segment (French, "Trimestre précédent") breaks within it rather
// than being cut off.
const CompactPresets = styled.div`
  margin-top: 16px;
  width: 100%;
  > div {
    padding: 2px;
  }
  button {
    flex: 1 1 0;
    min-width: 0;
    padding: 4px 6px !important;
    font-size: 12px !important;
    line-height: 1.2;
    letter-spacing: normal !important;
    text-transform: none !important;
    white-space: normal;
  }
`;

// The period step's presets as wide as its date fields above them.
const PeriodPresetsRow = styled.div`
  max-width: 560px;
`;

// "Van" and "Tot" side by side, stacked on a narrow screen.
export const PeriodFields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  max-width: 560px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

type TActivePreset = TPeriodPresetId | "other";

const presetLabel = (id: TPeriodPresetId): string => {
  switch (id) {
    case "last-month":
      return getLocalizedText("costs.modal.preset.last_month");
    case "this-quarter":
      return getLocalizedText("settlement.period.preset.current_quarter");
    case "last-quarter":
      return getLocalizedText("settlement.period.preset.previous_quarter");
    case "this-year":
      return getLocalizedText("settlement.period.preset.current_year");
    case "last-year":
      return getLocalizedText("settlement.period.preset.previous_year");
    default:
      return id;
  }
};

const activePreset = (
  period: { from: string; to: string },
  list: IPeriodPreset[],
): TActivePreset => {
  const match = list.find(p => p.from === period.from && p.to === period.to);
  return match ? match.id : "other";
};

// The period's quick picks (this year, last year, this quarter, last
// quarter) as small tabs under the date fields: the settlement's period
// step and the cost form share them (Lewis, 2026-10-19). The cost form
// opts in to "Vorige maand" as its first tab (withLastMonth, see
// periodPresets). A range typed by hand highlights none.
export const PeriodPresetTabs: React.FC<{
  name: string;
  period: { from: string; to: string };
  onSelect: (from: string, to: string) => void;
  withLastMonth?: boolean;
}> = ({ name, period, onSelect, withLastMonth = false }) => {
  const list = periodPresets({ now: new Date(), withLastMonth });
  return (
    <CompactPresets>
      <SegmentedControl<TActivePreset>
        name={name}
        size="small"
        options={list.map(p => ({ id: p.id, value: presetLabel(p.id) }))}
        selected={activePreset(period, list)}
        onChange={id => {
          const p = list.find(x => x.id === id);
          if (p) onSelect(p.from, p.to);
        }}
      />
    </CompactPresets>
  );
};

// "Dit huurcontract is al volledig afgerekend tot … in …", or "Rentio
// rekende dit huurcontract al af tot … in …", each settlement a link that
// opens it (Y05/Y06).
export const DepartureSettledNote: React.FC<{
  settled: ILeaseSettledThrough;
  // Where the period starts when the five-year limit moves it past the day
  // after (M25, see settledUntilCappedStart).
  cappedFrom?: string;
}> = ({ settled, cappedFrom }) => {
  const names = settled.settlements.map((settlement, index) => (
    <React.Fragment key={settlement.id}>
      {index > 0 && ", "}
      {settlement.propertyId ? (
        <Button
          appearance="link"
          url={`/properties/${settlement.propertyId}/costs?settlementId=${settlement.id}`}
        >
          {settlement.name}
        </Button>
      ) : (
        settlement.name
      )}
    </React.Fragment>
  ));
  // Settled past a lease end that moved earlier since (M07): the next
  // settlement corrects the days after it, so not "nothing is left".
  const fullKey = settled.pastEnd
    ? "settlement.departure.settled_past_end"
    : "settlement.departure.fully_settled";
  const untilKey = cappedFrom
    ? "settlement.departure.settled_until_capped"
    : "settlement.departure.settled_until";
  const text = getLocalizedText(settled.isFull ? fullKey : untilKey, {
    date: fmtDate(settled.until),
    settlements: "{{settlements}}",
    ...(settled.pastEnd ? { end: fmtDate(settled.pastEnd) } : {}),
    ...(cappedFrom ? { from: fmtDate(cappedFrom) } : {}),
  });
  const [before, after] = text.split("{{settlements}}");
  const body = (
    <span>
      {before}
      {names.length > 0 ? names : "—"}
      {after}
    </span>
  );
  return settled.isFull ? <Warn>{body}</Warn> : <Hint>{body}</Hint>;
};

export const StepPeriod: React.FC<StepContext> = ctx => {
  const { draft, saveDraft, invoices, settledInvoiceIds, contracts } = ctx;
  const departureLease = draft.departure
    ? contracts.find(c => c.id === draft.departure?.contractId)
    : undefined;

  // A departure runs up to its lease's last day (N28): "Tot" stays there,
  // so it never counts fewer provision months than the lease ran.
  const departureEnd = departureLease
    ? departureLease.endDate.slice(0, 10)
    : undefined;
  // A new period ticks its costs as a new settlement does (decision 1: only
  // the costs wholly within it; a departure every cost of its period but
  // the ones that wait for the yearly readings).
  const applyPeriod = (from: string, to: string) => {
    const newPeriod = { from, to: departureEnd ?? to };
    saveDraft({
      period: newPeriod,
      // A period that overlaps a confirmed settlement is suggested as
      // "Aanvulling …" (M22).
      isSupplementary:
        overlappingSettlementNames({
          period: newPeriod,
          settledPeriods: ctx.settledPeriods ?? [],
        }).length > 0,
      selectedInvoiceIds: getAutoSelectedInvoiceIds({
        invoices,
        excludedInvoiceIds: settledInvoiceIds,
        period: newPeriod,
        departure: draft.departure
          ? { methodByCategory: draft.distributionMethod }
          : undefined,
      }),
    });
  };
  const handlePeriodChange = (field: "from" | "to", value: string) => {
    applyPeriod(
      field === "from" ? value : draft.period.from,
      field === "to" ? value : draft.period.to,
    );
  };

  // The footer names what blocks "Volgende"; the field itself shows it too.
  // The name is the broker's (Lewis, 2026-10-16): "Naam" starts with a
  // suggestion from the kind and period, which follows the period until the
  // broker edits the field, and may not be left empty.
  const name = getDraftSettlementName(draft);
  const today = todayIsoDay();
  const errors = getPeriodStepErrors({ name, period: draft.period, today });
  // A lease that runs on for years (M17) starts its final settlement after
  // today: the banner above the step says to register its notice first, so
  // the field does not add a second, vaguer reason (M25).
  if (
    errors.from === "settlement.period.error.starts_in_future" &&
    departureEndsTooFarAhead({ leaseEnd: departureEnd, today })
  ) {
    delete errors.from;
  }
  // The day the period starts when the five-year limit moved it past the
  // day after the last settlement (M25).
  const cappedFrom =
    departureEnd && ctx.departureSettled && !ctx.departureSettled.isFull
      ? settledUntilCappedStart({
          until: ctx.departureSettled.until,
          departureDay: departureEnd,
          today,
        })
      : undefined;
  // The confirmed settlements this period overlaps (M22): a settlement of
  // the same period only takes what is still open.
  const overlapping = draft.departure
    ? []
    : overlappingSettlementNames({
        period: draft.period,
        settledPeriods: ctx.settledPeriods ?? [],
      });
  // Months after today are not due yet, yet D7 counts them (decision 15).
  const runsPastToday = periodRunsPastToday({ period: draft.period, today });
  // A departure that starts before the first cost Rentio has for the unit
  // and its building: those months were probably settled outside Rentio (a
  // lease entered later), so the broker checks the start (D8). Information
  // only, the broker may keep it.
  const firstCostDay = departureLease
    ? startsBeforeFirstCost({ from: draft.period.from, costs: invoices })
    : undefined;
  // The whole lease period lies before that first cost (M47): no start date
  // helps, there is simply no cost of it in Rentio yet.
  const isWhollyBeforeFirstCost =
    !!firstCostDay &&
    !!endsBeforeFirstCost({ to: draft.period.to, costs: invoices });

  return (
    <div>
      <StepHeader>{getLocalizedText("costs.field.period")}</StepHeader>
      <div style={{ marginBottom: 16 }}>
        <TextStyle variation="subdued">
          {getLocalizedText("settlement.period.intro")}
        </TextStyle>
      </div>
      {departureLease && !ctx.departureSettled?.isFull && (
        // A departure settlement: one lease, up to its last day.
        <div style={{ marginBottom: 16 }}>
          <Hint>
            {getLocalizedText("settlement.departure.intro", {
              tenant: departureLease.tenantName,
              until: fmtDate(departureLease.endDate),
            })}
          </Hint>
        </div>
      )}
      {departureLease && ctx.departureSettled && (
        // Up to when Rentio already settled the lease, and where (Y05,
        // Y06): the period starts after it, and a lease settled up to its
        // last day needs no more than a final settlement of € 0.
        <div style={{ marginBottom: 16 }}>
          <DepartureSettledNote
            settled={ctx.departureSettled}
            cappedFrom={cappedFrom}
          />
        </div>
      )}
      <div style={{ maxWidth: 560, marginBottom: 24 }}>
        <TextField
          name="settlement-name"
          label={getLocalizedText("settlement.period.name_label")}
          helpText={getLocalizedText("settlement.period.name_label.help")}
          placeholder={getSuggestedSettlementName(draft)}
          value={name}
          maxLength={SETTLEMENT_NAME_MAX_LENGTH}
          error={localizeError(errors.name)}
          onChange={(e: any) => saveDraft({ name: e.target.value })}
        />
      </div>
      <PeriodFields>
        <IsoDateField
          id="period-from"
          label={getLocalizedText("costs.field.from")}
          value={draft.period.from}
          onChange={value => handlePeriodChange("from", value)}
          error={localizeError(errors.from)}
        />
        <IsoDateField
          id="period-to"
          label={getLocalizedText("costs.field.to")}
          value={departureEnd ?? draft.period.to}
          onChange={value => handlePeriodChange("to", value)}
          error={localizeError(errors.to)}
          disabled={!!departureEnd}
        />
      </PeriodFields>
      {runsPastToday && (
        <div style={{ marginTop: 16, maxWidth: 560 }}>
          <Warn>
            {getLocalizedText("settlement.period.future_months_warning", {
              today: fmtDate(today),
            })}
          </Warn>
        </div>
      )}
      {overlapping.length > 0 && (
        <div style={{ marginTop: 16, maxWidth: 560 }}>
          <Hint>
            {getLocalizedText("settlement.period.overlaps_settled", {
              settlements: overlapping.join(", "),
            })}
          </Hint>
        </div>
      )}
      {firstCostDay && (
        <div style={{ marginTop: 16, maxWidth: 560 }}>
          <Hint>
            {getLocalizedText(
              isWhollyBeforeFirstCost
                ? "settlement.period.no_cost_for_lease_period"
                : "settlement.period.before_first_cost",
              { date: fmtDate(firstCostDay) },
            )}
          </Hint>
        </div>
      )}
      {/* A departure's period ends with its lease (N28): no presets that
          would move its end. */}
      {!departureEnd && (
        <PeriodPresetsRow>
          <PeriodPresetTabs
            name="period-preset"
            period={draft.period}
            onSelect={applyPeriod}
          />
        </PeriodPresetsRow>
      )}
    </div>
  );
};
